/*
 * Copyright Elasticsearch B.V. and/or licensed to Elasticsearch B.V. under one
 * or more contributor license agreements. Licensed under the Elastic License
 * 2.0; you may not use this file except in compliance with the Elastic License
 * 2.0.
 */

// DO NOT MODIFY THIS FILE BY HAND. IT IS GENERATED BY
// `yarn workspace @elastic/esql-definitions generate` FROM
// `elasticsearch/esql/x-pack-esql/definition/functions/trange.json`.

import type { FunctionDefinition } from '../../definition_types';

/**
 * Filters data for the given time range using the @timestamp attribute.
 *
 * @example
 * FROM k8s
 * | WHERE TRANGE(1h)
 * | KEEP @timestamp
 *
 * @example
 * FROM k8s
 * | WHERE TRANGE("2024-05-10T00:17:14.000Z", "2024-05-10T00:18:33.000Z")
 * | SORT @timestamp
 * | KEEP @timestamp
 * | LIMIT 10
 *
 * @example
 * FROM k8s
 * | WHERE TRANGE(to_datetime("2024-05-10T00:17:14Z"), to_datetime("2024-05-10T00:18:33Z"))
 * | SORT @timestamp
 * | KEEP @timestamp
 * | LIMIT 10
 *
 * @example
 * FROM k8s
 * | WHERE TRANGE(to_datetime("2024-05-10T00:17:14.000Z"), to_datetime("2024-05-10T00:18:33.000Z"))
 * | SORT @timestamp
 * | KEEP @timestamp
 * | LIMIT 10
 *
 * @example
 * FROM k8s
 * | WHERE TRANGE(1715300236000, 1715300282000)
 * | SORT @timestamp
 * | KEEP @timestamp
 * | LIMIT 10
 */
const definition: FunctionDefinition = {
  type: 'scalar',
  name: 'trange',
  signatures: [
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'date',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'end_time',
          type: 'date',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'date_nanos',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'end_time',
          type: 'date_nanos',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'date_period',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'keyword',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'end_time',
          type: 'keyword',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'long',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
        {
          name: 'end_time',
          type: 'long',
          optional: true,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
    {
      params: [
        {
          name: 'start_time_or_offset',
          type: 'time_duration',
          optional: false,
          hint: {
            kind: 'constant',
          },
        },
      ],
      variadic: false,
      returnType: 'boolean',
    },
  ],
  preview: false,
  snapshotOnly: false,
};

export default definition;
